import { CreateDispatchResponse, ModelGroup, OfferType, OptimizationResult } from '../../services/SpecialOffersService';

export type WizardStep = 'account' | 'models' | 'optimization' | 'confirmation';

export type DraftStatus = 'draft' | 'ready' | 'sent';

export interface SpecialOffersDraft {
  id: string;
  title: string;
  status: DraftStatus;
  createdAt: string;
  updatedAt: string;
  currentStep: WizardStep;
  selectedAccount: string;
  selectedAccountData: any;
  budget: number;
  availableModels: ModelGroup[];
  selectedModels: string[];
  modelCounts: Record<string, number>;
  availableOffers: OfferType[];
  selectedOffer: OfferType | null;
  optimizationResult: OptimizationResult | null;
  dispatchResponse: CreateDispatchResponse | null;
  availabilityMap: Record<number, { isAvailable: boolean; reason?: string | null }>;
  expiresAt: number | null;
  campaignDays: number;
  autoRenew: boolean;
  discountValue: number | '';
  minAvailableDiscount: number;
  maxAvailableDiscount: number;
  campaignMode: 'fixed_items' | 'dynamic_models' | null;
  parentCampaignId: string | null;
}

export interface DraftsState {
  drafts: SpecialOffersDraft[];
  activeDraftId: string | null;
}

export type SpecialOffersDraftsCommand =
  | { type: 'create' }
  | { type: 'select'; draftId: string }
  | { type: 'delete'; draftId: string };

const DRAFTS_STORAGE_KEY = 'special_offers_drafts_v1';
const ACTIVE_DRAFT_STORAGE_KEY = 'special_offers_active_draft_id_v1';
const LEGACY_SESSION_KEY = 'special_offers_wizard_state';
export const SPECIAL_OFFERS_DRAFTS_SYNC_EVENT = 'special-offers-drafts-sync';
export const SPECIAL_OFFERS_DRAFTS_COMMAND_EVENT = 'special-offers-drafts-command';

const createDraftId = (): string =>
  `draft_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

export const createEmptyDraft = (): SpecialOffersDraft => {
  const now = new Date().toISOString();
  return {
    id: createDraftId(),
    title: 'Новая рассылка',
    status: 'draft',
    createdAt: now,
    updatedAt: now,
    currentStep: 'account',
    selectedAccount: '',
    selectedAccountData: null,
    budget: 3000,
    availableModels: [],
    selectedModels: [],
    modelCounts: {},
    availableOffers: [],
    selectedOffer: null,
    optimizationResult: null,
    dispatchResponse: null,
    availabilityMap: {},
    expiresAt: null,
    campaignDays: 7,
    autoRenew: false,
    discountValue: '',
    minAvailableDiscount: 0,
    maxAvailableDiscount: 0,
    campaignMode: null,
    parentCampaignId: null,
  };
};

const safeParse = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const normalizeDraft = (draft: Partial<SpecialOffersDraft> | null | undefined): SpecialOffersDraft => {
  const base = createEmptyDraft();
  if (!draft || typeof draft !== 'object') {
    return base;
  }

  return {
    ...base,
    ...draft,
    id: typeof draft.id === 'string' && draft.id ? draft.id : base.id,
    title: typeof draft.title === 'string' && draft.title ? draft.title : base.title,
    status: draft.status === 'ready' || draft.status === 'sent' ? draft.status : 'draft',
    createdAt: typeof draft.createdAt === 'string' && draft.createdAt ? draft.createdAt : base.createdAt,
    updatedAt: typeof draft.updatedAt === 'string' && draft.updatedAt ? draft.updatedAt : base.updatedAt,
    currentStep: draft.currentStep === 'models' || draft.currentStep === 'optimization' || draft.currentStep === 'confirmation'
      ? draft.currentStep
      : 'account',
    selectedAccount: typeof draft.selectedAccount === 'string' ? draft.selectedAccount : '',
    selectedModels: Array.isArray(draft.selectedModels) ? draft.selectedModels : [],
    modelCounts: draft.modelCounts && typeof draft.modelCounts === 'object' ? draft.modelCounts : {},
    availableModels: Array.isArray(draft.availableModels) ? draft.availableModels : [],
    availableOffers: Array.isArray(draft.availableOffers) ? draft.availableOffers : [],
    selectedOffer: draft.selectedOffer ?? null,
    optimizationResult: draft.optimizationResult ?? null,
    dispatchResponse: draft.dispatchResponse ?? null,
    availabilityMap: draft.availabilityMap && typeof draft.availabilityMap === 'object' ? draft.availabilityMap : {},
    expiresAt: typeof draft.expiresAt === 'number' ? draft.expiresAt : null,
    campaignDays: typeof draft.campaignDays === 'number' ? draft.campaignDays : base.campaignDays,
    autoRenew: Boolean(draft.autoRenew),
    discountValue: typeof draft.discountValue === 'number' ? draft.discountValue : '',
    minAvailableDiscount: typeof draft.minAvailableDiscount === 'number' ? draft.minAvailableDiscount : 0,
    maxAvailableDiscount: typeof draft.maxAvailableDiscount === 'number' ? draft.maxAvailableDiscount : 0,
    campaignMode: draft.campaignMode === 'fixed_items' || draft.campaignMode === 'dynamic_models'
      ? draft.campaignMode
      : null,
    parentCampaignId: typeof draft.parentCampaignId === 'string' && draft.parentCampaignId
      ? draft.parentCampaignId
      : null,
  };
};

const migrateLegacyDraft = (): SpecialOffersDraft | null => {
  const legacy = safeParse<Record<string, any>>(sessionStorage.getItem(LEGACY_SESSION_KEY));
  if (!legacy || typeof legacy !== 'object') {
    return null;
  }

  const migrated = normalizeDraft({
    ...legacy,
    id: createDraftId(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // ignore
  }

  return migrated;
};

export const loadDraftsState = (): DraftsState => {
  const storedDrafts = safeParse<SpecialOffersDraft[]>(localStorage.getItem(DRAFTS_STORAGE_KEY));
  const storedActiveDraftId = localStorage.getItem(ACTIVE_DRAFT_STORAGE_KEY);

  let drafts = Array.isArray(storedDrafts)
    ? storedDrafts.map(normalizeDraft)
    : [];

  if (drafts.length === 0) {
    const migrated = migrateLegacyDraft();
    if (migrated) {
      drafts = [migrated];
    }
  }

  const activeDraftId = drafts.length > 0 && drafts.some((draft) => draft.id === storedActiveDraftId)
    ? storedActiveDraftId
    : (drafts[0]?.id ?? null);

  return {
    drafts,
    activeDraftId,
  };
};

export const persistDraftsState = (state: DraftsState): void => {
  try {
    localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(state.drafts));
    if (state.activeDraftId) {
      localStorage.setItem(ACTIVE_DRAFT_STORAGE_KEY, state.activeDraftId);
    } else {
      localStorage.removeItem(ACTIVE_DRAFT_STORAGE_KEY);
    }
  } catch {
    // ignore quota/storage errors
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<DraftsState>(SPECIAL_OFFERS_DRAFTS_SYNC_EVENT, { detail: state }));
  }
};

export const dispatchSpecialOffersDraftsCommand = (command: SpecialOffersDraftsCommand): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<SpecialOffersDraftsCommand>(SPECIAL_OFFERS_DRAFTS_COMMAND_EVENT, { detail: command }));
};
